/**
 * authFetch — standalone authenticated fetch for class-based services.
 *
 * React hooks (useApiHeaders) can't be used in class singletons.
 * This module acquires a Bearer token from the exported MSAL instance,
 * decodes the tenant ID from the JWT access token at runtime (Option B),
 * and attaches both to every request.
 *
 * Tenant resolution (runtime, not build-time):
 *   1. Acquire access token from MSAL
 *   2. Decode JWT payload → read VITE_TENANT_CLAIM_NAME claim (set after E1)
 *   3. Dev fallback: if claim absent AND Vite dev server → VITE_CUSTOMER_ID
 *   4. Production: if claim absent → throw with E1 setup instructions
 *
 * The x-user-role header is intentionally NOT included — TenantMiddleware
 * derives role from the userTenantRoles Cosmos DB container, never from a
 * caller-controlled header.
 */

import { msalInstance } from '../App';
import { apiRequest } from '../config/authConfig';
import { getTenantIdOrThrow, decodeJwtPayload } from '../utils/jwtTenantResolver';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';

/**
 * Acquire a Bearer token from the MSAL singleton.
 * Returns null if no account is signed in (pre-auth pages, dev mode).
 */
async function acquireToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account: accounts[0],
    });
    return response.accessToken;
  } catch {
    // Silent acquisition failed — try interactive
    try {
      const response = await msalInstance.acquireTokenPopup(apiRequest);
      return response.accessToken;
    } catch {
      return null;
    }
  }
}

/**
 * Authenticated fetch wrapper for class-based services.
 * Attaches Bearer token + x-tenant-id to every request.
 *
 * @param path  API path relative to API_BASE (e.g. '/configuration')
 * @param init  Standard RequestInit options (method, body, headers, etc.)
 */
export async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await acquireToken();
  const tenantId = getTenantIdOrThrow(token);

  // Decode the OID from the access token so the backend can use it as a verified
  // fallback identity (body.validatedBy || x-user-id) without trusting a caller-supplied string.
  const oidClaim = token ? decodeJwtPayload(token)?.oid : undefined;
  const userOid = typeof oidClaim === 'string' ? oidClaim : undefined;

  const headers: Record<string, string> = {
    'x-tenant-id': tenantId,
    ...(init.headers as Record<string, string> || {}),
  };

  // Only set Content-Type for non-FormData bodies. FormData needs the browser
  // to set the multipart boundary automatically.
  if (!(init.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (userOid) {
    headers['x-user-id'] = userOid;
  }

  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

/** The API base URL, for services that need to construct URLs manually. */
export { API_BASE };
